class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
    this.updateOptions();
    this.updateMasterId();
    this.updateOptionAvailability();
    
    // Initialize from URL variant parameter
    this.initializeFromURL();
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.updateOptionAvailability();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.updateVariantButton();
      this.renderProductInfo();
    }

    this.dispatchEvent(new Event('variant::changed'));
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    const variantData = this.getVariantData();
    
    if (!Array.isArray(variantData)) {
      console.error('Variant data is not an array:', variantData);
      return;
    }
    
    this.currentVariant = variantData.find((variant) => {
      const matches = !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
      return matches;
    });
  }

  updateOptionAvailability() {
    const variantData = this.getVariantData();
    if (!variantData) return;

    // Handle radio buttons (variant-radios)
    const fieldsets = Array.from(this.querySelectorAll('fieldset.product-form__input'));
    
    fieldsets.forEach((fieldset, optionIndex) => {
      // Get all option values for this fieldset
      const optionWrappers = Array.from(fieldset.querySelectorAll('.form-option'));
      
      optionWrappers.forEach((wrapper) => {
        const input = wrapper.querySelector('input[type="radio"]');
        if (!input) return;
        
        const optionValue = input.value;
        
        // Check if any variant with this option value is available
        // considering the currently selected options from other fieldsets
        const isAvailable = variantData.some((variant) => {
          // Check if this variant matches the current option value at this position
          if (variant.options[optionIndex] !== optionValue) return false;
          
          // Check if it matches all OTHER selected options
          let matchesOtherOptions = true;
          this.options.forEach((selectedOption, selectedIndex) => {
            if (selectedIndex !== optionIndex && variant.options[selectedIndex] !== selectedOption) {
              matchesOtherOptions = false;
            }
          });
          
          // The option is available if there's at least one matching variant that's available
          return matchesOtherOptions && variant.available;
        });
        
        // Update the UI based on availability - only add/remove class
        if (isAvailable) {
          wrapper.classList.remove('unavailable');
        } else {
          wrapper.classList.add('unavailable');
        }
      });
    });
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;
    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    );
    if (!newMedia) return;
    const parent = newMedia.parentElement;
    parent.prepend(newMedia);
    window.setTimeout(() => { parent.scroll(0, 0) });
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantButton() {
    const button = document.getElementById(`product-form-${this.dataset.section}`).querySelector('[name="add"]');
    button.setAttribute('data-id', this.currentVariant.id);
  }

  renderProductInfo() {
    // Use local JSON data instead of fetch
    const variantData = this.getVariantData();
    const selectedVariant = variantData.find(v => v.id === this.currentVariant.id);
    
    if (selectedVariant) {
      // Update price using local data
      const priceElement = document.getElementById(`price-${this.dataset.section}`);
      if (priceElement) {
        // Format prices (convert from cents to dollars)
        const price = selectedVariant.price / 100;
        const compareAtPrice = selectedVariant.compare_at_price ? selectedVariant.compare_at_price / 100 : null;
        
        // Format with thousands separators
        const formatPrice = (amount) => {
          return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(amount);
        };
        
        const formattedPrice = formatPrice(price);
        const formattedComparePrice = compareAtPrice ? formatPrice(compareAtPrice) : null;
        
        // Check if there's a discount (compare_at_price > price)
        const hasDiscount = compareAtPrice && compareAtPrice > price;
        
        // Generate HTML similar to product-price.liquid structure
        let priceHTML = '';
        if (hasDiscount) {
          // Show both compare-at price and current price (similar to product-price.liquid)
          priceHTML = `
            <div class="price product-price">
              <div class="price__compare-at visible" data-price-compare-container>
                <span class="visually-hidden">Precio original</span>
                <span class="money price__compare-at--single" data-price-compare>
                  ${formattedComparePrice}
                </span>
              </div>
              <div class="price__current price__current--on-sale" data-price-container>
                <span class="visually-hidden">Precio actual</span>
                <span class="money" data-price>
                  ${formattedPrice}
                </span>
              </div>
            </div>
          `;
        } else {
          // Show only current price
          priceHTML = `
            <div class="price product-price">
              <div class="price__current" data-price-container>
                <span class="money" data-price>
                  ${formattedPrice}
                </span>
              </div>
            </div>
          `;
        }
        
        // Update price element
        priceElement.innerHTML = priceHTML;
        
        // Show price element
        priceElement.classList.remove('visibility-hidden');
        
        // Also update sticky form price if it exists
        const stickyPriceElement = document.getElementById(`sticky-price-${this.dataset.section}`);
        if (stickyPriceElement) {
          stickyPriceElement.innerHTML = priceHTML;
          stickyPriceElement.classList.remove('visibility-hidden');
        }
      }
      
      // Update option labels when variant exists
      const $currentVariantNames = document.querySelectorAll('main-product .form__label-selected-value');
      const $currentSizeVariantName = document.querySelector('main-product .main-product-variant-name');

      // Update each option label with its specific value
      if ($currentVariantNames.length > 0 && selectedVariant) {
        $currentVariantNames.forEach(($element) => {
          const optionPosition = $element.getAttribute('data-option-position');
          if (optionPosition) {
            const optionValue = selectedVariant[`option${optionPosition}`];
            if (optionValue) {
              $element.textContent = optionValue;
            }
          }
        });
      }

      if ($currentSizeVariantName && selectedVariant) {
        $currentSizeVariantName.textContent = selectedVariant.title;
      }
    } else {
      
      // Fallback to fetch if variant not found in local data
      const fetchUrl = `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`;
      
      fetch(fetchUrl)
        .then((response) => {
          return response.text();
        })
        .then((responseText) => {
          const id = `price-${this.dataset.section}`;
          
          const html = new DOMParser().parseFromString(responseText, 'text/html')
          const destination = document.getElementById(id);
          const source = html.getElementById(id);

          if (source && destination) {
            destination.innerHTML = source.innerHTML;
          }

          document.getElementById(`price-${this.dataset.section}`).classList.remove('visibility-hidden');
          
          // Also update sticky form price if it exists
          const stickyPriceElement = document.getElementById(`sticky-price-${this.dataset.section}`);
          if (stickyPriceElement && source) {
            stickyPriceElement.innerHTML = source.innerHTML;
            stickyPriceElement.classList.remove('visibility-hidden');
          }
          
          this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);

          const $currentVariantNames = document.querySelectorAll('main-product .form__label-selected-value');
          const $currentSizeVariantName = document.querySelector('main-product .main-product-variant-name');

          // Update each option label with its specific value
          if ($currentVariantNames.length > 0 && this.currentVariant) {
            $currentVariantNames.forEach(($element) => {
              const optionPosition = $element.getAttribute('data-option-position');
              if (optionPosition) {
                const optionValue = this.currentVariant[`option${optionPosition}`];
                if (optionValue) {
                  $element.textContent = optionValue;
                }
              }
            });
          }

          if (this.currentVariant && $currentSizeVariantName) {
            $currentSizeVariantName.textContent = this.currentVariant.title;
          }
        })
        .catch((error) => {
          console.error('Error in renderProductInfo:', error);
        });
    }

    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
    .then((response) => response.text())
    .then((responseText) => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      const $newBadges = html.querySelector('main-product .main-product-badges');
      const $currentBadges = document.querySelector('main-product .main-product-badges');

      if ($newBadges && $currentBadges) {
        $currentBadges.innerHTML = $newBadges.innerHTML;
      }

      // Also update sticky form badges if they exist
      const $stickyBadges = document.querySelector('sticky-form .main-product-badges');
      if ($newBadges && $stickyBadges) {
        $stickyBadges.innerHTML = $newBadges.innerHTML;
      }
    });
    
    // Update add button state and variant name for local data path
    this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
    
    // Update variant name display
    const $currentVariantNames = document.querySelectorAll('main-product .form__label-selected-value');
    const $currentSizeVariantName = document.querySelector('main-product .main-product-variant-name');

    // Update each option label with its specific value
    if ($currentVariantNames.length > 0 && selectedVariant) {
      $currentVariantNames.forEach(($element) => {
        const optionPosition = $element.getAttribute('data-option-position');
        if (optionPosition) {
          const optionValue = selectedVariant[`option${optionPosition}`];
          if (optionValue) {
            $element.textContent = optionValue;
          }
        }
      });
    }

    if ($currentSizeVariantName && selectedVariant) {
      $currentSizeVariantName.textContent = selectedVariant.title;
    }
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const addButton = document.getElementById(`product-form-${this.dataset.section}`).querySelector('[name="add"]');
    const $quantitySelector = document.querySelector('.quantity-selector');

    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', true);
      if (text) addButton.textContent = text;
      $quantitySelector.classList.add('disabled');
    } else {
      addButton.removeAttribute('disabled');
      addButton.textContent = window.variantStrings.addToCart;
      $quantitySelector.classList.remove('disabled');
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const addButton = document.getElementById(`product-form-${this.dataset.section}`).querySelector('[name="add"]');
    if (!addButton) return;
    addButton.textContent = window.variantStrings.unavailable;
    document.getElementById(`price-${this.dataset.section}`).classList.add('visibility-hidden');
    
    // Clear the variant input value when variant doesn't exist
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (input) {
        input.value = '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    
    // Update option labels with selected values even when variant doesn't exist
    this.updateOptionLabels();
  }

  updateOptionLabels() {
    const $currentVariantNames = document.querySelectorAll('main-product .form__label-selected-value');
    const $currentSizeVariantName = document.querySelector('main-product .main-product-variant-name');

    // Update each option label with its specific value
    if ($currentVariantNames.length > 0 && this.options) {
      $currentVariantNames.forEach(($element) => {
        const optionPosition = $element.getAttribute('data-option-position');
        if (optionPosition) {
          const optionValue = this.options[optionPosition - 1];
          if (optionValue) {
            $element.textContent = optionValue;
          }
        }
      });
    }

    if ($currentSizeVariantName && this.options) {
      // When variant doesn't exist, show the combined options
      $currentSizeVariantName.textContent = this.options.join(' / ');
    }
  }

  initializeFromURL() {
    // Check if there's a variant parameter in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    
    if (variantId) {
      // Find the variant in our data
      const variantData = this.getVariantData();
      const targetVariant = variantData.find(v => v.id.toString() === variantId);
      
      if (targetVariant) {
        // Select the corresponding radio buttons/selects
        this.selectVariantOptions(targetVariant);
        
        // Update the current variant and UI
        this.currentVariant = targetVariant;
        this.renderProductInfo();
      }
    }
  }
  
  selectVariantOptions(variant) {
    // For radio buttons (VariantRadios)
    const radioInputs = this.querySelectorAll('input[type="radio"]');
    radioInputs.forEach(radio => {
      const optionIndex = parseInt(radio.dataset.optionPosition) - 1;
      if (optionIndex >= 0 && optionIndex < variant.options.length) {
        if (radio.value === variant.options[optionIndex]) {
          radio.checked = true;
        }
      }
    });
    
    // For select elements (VariantSelects)
    const selectElements = this.querySelectorAll('select');
    selectElements.forEach(select => {
      const optionIndex = parseInt(select.dataset.optionPosition) - 1;
      if (optionIndex >= 0 && optionIndex < variant.options.length) {
        select.value = variant.options[optionIndex];
      }
    });
  }

  getVariantData() {
    if (!this.variantData) {
      const jsonElement = this.querySelector('[type="application/json"]');
      console.log(jsonElement);
      if (jsonElement) {
        try {
          this.variantData = JSON.parse(jsonElement.textContent);
        } catch (error) {
          console.error('Error parsing JSON:', error);
        }
      } else {
        console.error('No JSON element found');
      }
    }
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);

var ProductForm = class extends HTMLFormElement {
  connectedCallback() {
    this.id.disabled = false;
    this.addEventListener("submit", this._onSubmit.bind(this));
  }
  async _onSubmit(event) {
    event.preventDefault();
    if (!this.checkValidity()) {
      this.reportValidity();
      return;
    }
    const submitButtons = Array.from(this.elements).filter((button) => button.type === "submit");
    submitButtons.forEach((submitButton) => {
      submitButton.setAttribute("disabled", "disabled");
      submitButton.setAttribute("aria-busy", "true");
    });
    const productForm = new FormData(this);
    productForm.append("sections", ["mini-cart"]);
    productForm.delete("option1");
    productForm.delete("option2");
    productForm.delete("option3");
    const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
      body: productForm,
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest"
        // This is needed for the endpoint to properly return 422
      }
    });
    submitButtons.forEach((submitButton) => {
      submitButton.removeAttribute("disabled");
      submitButton.removeAttribute("aria-busy");
    });
    const responseJson = await response.json();
    if (response.ok) {
      if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
        return window.location.href = `${Shopify.routes.root}cart`;
      }
      this.dispatchEvent(new CustomEvent("variant:added", {
        bubbles: true,
        detail: {
          variant: responseJson.hasOwnProperty("items") ? responseJson["items"][0] : responseJson
        }
      }));
      fetch(`${window.themeVariables.routes.cartUrl}.js`).then(async (response2) => {
        const cartContent = await response2.json();
        document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
          bubbles: true,
          detail: {
            cart: cartContent
          }
        }));
        cartContent["sections"] = responseJson["sections"];
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart: window.themeVariables.settings.cartType === "drawer" && this.closest(".drawer") === null
          }
        }));
      });
    } else {
      this.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }
    this.dispatchEvent(new CustomEvent("cart-notification:show", {
      bubbles: true,
      cancelable: true,
      detail: {
        status: response.ok ? "success" : "error",
        error: responseJson["description"] || ""
      }
    }));
  }
};
window.customElements.define("product-form", ProductForm, { extends: "form" });

// js/common/overlay/dialog-element.js
// Simplified version without external dependencies

// Helper function for waiting for events
function waitForEvent(element, eventName) {
  return new Promise(resolve => {
    element.addEventListener(eventName, resolve, { once: true });
  });
}
var DialogElement = class _DialogElement extends HTMLElement {
  static get observedAttributes() {
    return ["id", "open"];
  }
  static #lockLayerCount = 0;
  #isLocked = false;
  constructor() {
    super();
    this.addEventListener("dialog:force-close", (event) => {
      this.hide();
      event.stopPropagation();
    });
  }
  connectedCallback() {
    if (this.id) {
      // Initialize delegate first
      this.delegate.off();
      this.delegate.on("click", `[aria-controls="${this.id}"]`, this._onToggleClicked.bind(this));
    }
    this._abortController = new AbortController();
    this.setAttribute("role", "dialog");
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.show(!event.detail.load), { signal: this._abortController.signal });
      this.addEventListener("shopify:block:deselect", this.hide, { signal: this._abortController.signal });
      this._shopifySection = this._shopifySection || this.closest(".shopify-section");
      if (this._shopifySection) {
        if (this.hasAttribute("handle-section-events")) {
          this._shopifySection.addEventListener("shopify:section:select", (event) => this.show(!event.detail.load), { signal: this._abortController.signal });
          this._shopifySection.addEventListener("shopify:section:deselect", this.hide.bind(this), { signal: this._abortController.signal });
        }
        this._shopifySection.addEventListener("shopify:section:unload", () => this.remove(), { signal: this._abortController.signal });
      }
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
    this.delegate.off();
    this.focusTrap?.deactivate({ onDeactivate: () => {
    } });
    if (this.#isLocked) {
      this.#isLocked = false;
      document.documentElement.classList.toggle("lock", --_DialogElement.#lockLayerCount > 0);
    }
  }
  show(animate19 = true) {
    if (this.open) {
      return;
    }
    this.setAttribute("open", animate19 ? "" : "immediate");
    return waitForEvent(this, "dialog:after-show");
  }
  hide() {
    if (!this.open) {
      return;
    }
    this.removeAttribute("open");
    return waitForEvent(this, "dialog:after-hide");
  }
  get delegate() {
    if (!this._delegate) {
      this._delegate = {
        off: () => {
          // Remove existing listeners if any
          if (this._delegateHandler) {
            document.body.removeEventListener('click', this._delegateHandler);
          }
        },
        on: (event, selector, handler) => {
          this._delegateHandler = (e) => {
            // Check if the target matches the selector OR if it's inside an element that matches
            const target = e.target.closest(selector);
            if (target) {
              handler(e);
            }
          };
          document.body.addEventListener(event, this._delegateHandler);
        }
      };
    }
    return this._delegate;
  }
  get controls() {
    return Array.from(this.getRootNode().querySelectorAll(`[aria-controls="${this.id}"]`));
  }
  get open() {
    return this.hasAttribute("open");
  }
  get shouldTrapFocus() {
    return true;
  }
  get shouldLock() {
    return false;
  }
  /**
   * Sometimes (especially for drawer) we need to ensure that an element is on top of everything else. To do that,
   * we need to move the element to the body. We are doing that on open, and then restore the initial position on
   * close
   */
  get shouldAppendToBody() {
    return false;
  }
  get initialFocus() {
    return this.hasAttribute("initial-focus") ? this.getAttribute("initial-focus") : this.hasAttribute("tabindex") ? this : this.querySelector('input:not([type="hidden"])') || false;
  }
  get preventScrollWhenTrapped() {
    return true;
  }
  get focusTrap() {
    if (!this._focusTrap) {
      this._focusTrap = {
        activate: () => {},
        deactivate: () => {}
      };
    }
    return this._focusTrap;
  }
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "id":
        if (newValue) {
          this.delegate.off();
          this.delegate.on("click", `[aria-controls="${this.id}"]`, this._onToggleClicked.bind(this));
        }
        break;
      case "open":
        this.controls.forEach((toggle) => toggle.setAttribute("aria-expanded", newValue === null ? "false" : "true"));
        if (oldValue === null && (newValue === "" || newValue === "immediate")) {
          this.removeAttribute("inert");
          this._originalParentBeforeAppend = null;
          if (this.shouldAppendToBody && this.parentElement !== document.body) {
            this._originalParentBeforeAppend = this.parentElement;
            document.body.append(this);
          }
          const showTransitionPromise = this._showTransition(newValue !== "immediate") || Promise.resolve();
          showTransitionPromise.then(() => {
            this.dispatchEvent(new CustomEvent("dialog:after-show", { bubbles: true }));
          });
          if (this.shouldTrapFocus) {
            this.focusTrap.activate({
              checkCanFocusTrap: () => showTransitionPromise
            });
          }
          if (this.shouldLock) {
            _DialogElement.#lockLayerCount += 1;
            this.#isLocked = true;
            document.documentElement.classList.add("lock");
          }
        } else if (oldValue !== null && newValue === null) {
          this.setAttribute("inert", "");
          const hideTransitionPromise = this._hideTransition() || Promise.resolve();
          hideTransitionPromise.then(() => {
            if (this.parentElement === document.body && this._originalParentBeforeAppend) {
              this._originalParentBeforeAppend.appendChild(this);
              this._originalParentBeforeAppend = null;
            }
            this.dispatchEvent(new CustomEvent("dialog:after-hide", { bubbles: true }));
          });
          this.focusTrap?.deactivate({
            checkCanReturnFocus: () => hideTransitionPromise
          });
          if (this.shouldLock) {
            this.#isLocked = false;
            document.documentElement.classList.toggle("lock", --_DialogElement.#lockLayerCount > 0);
          }
        }
        this.dispatchEvent(new CustomEvent("toggle", { bubbles: true }));
        break;
    }
  }
  /* Those methods are used to perform custom show/hide transition, and must return a promise */
  _showTransition(animate19 = true) {
  }
  _hideTransition() {
  }
  /* By default, a focus element is deactivated when you click outside it */
  _allowOutsideClick(event) {
    if ("TouchEvent" in window && event instanceof TouchEvent) {
      return this._allowOutsideClickTouch(event);
    } else {
      return this._allowOutsideClickMouse(event);
    }
  }
  _allowOutsideClickTouch(event) {
    event.target.addEventListener("touchend", (subEvent) => {
      const endTarget = document.elementFromPoint(subEvent.changedTouches.item(0).clientX, subEvent.changedTouches.item(0).clientY);
      if (!this.contains(endTarget)) {
        this.hide();
      }
    }, { once: true });
    return false;
  }
  _allowOutsideClickMouse(event) {
    if (event.type !== "click") {
      return false;
    }
    if (!this.contains(event.target)) {
      this.hide();
    }
    let target = event.target, closestControl = event.target.closest("[aria-controls]");
    if (closestControl && closestControl.getAttribute("aria-controls") === this.id) {
      target = closestControl;
    }
    return this.id !== target.getAttribute("aria-controls");
  }
  _onToggleClicked(event) {
    event?.preventDefault();
    this.open ? this.hide() : this.show();
  }
};
var CloseButton = class extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("dialog:force-close", { bubbles: true, cancelable: true, composed: true })));
  }
};
if (!window.customElements.get("dialog-element")) {
  window.customElements.define("dialog-element", DialogElement);
}
if (!window.customElements.get("close-button")) {
  window.customElements.define("close-button", CloseButton, { extends: "button" });
}

// js/common/overlay/drawer.js
import { animate as motionAnimate, timeline as motionTimeline } from "vendor";
var reduceDrawerAnimation = window.matchMedia("(prefers-reduced-motion: reduce)").matches || window.themeVariables.settings.reduceDrawerAnimation;
var Drawer = class extends DialogElement {
  constructor() {
    super();
    const template2 = document.getElementById(this.template).content.cloneNode(true);
    this.attachShadow({ mode: "open" }).appendChild(template2);
    this.shadowRoot.addEventListener("slotchange", (event) => this._updateSlotVisibility(event.target));
  }
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("aria-modal", "true");
    this.shadowRoot.querySelector('[part="overlay"]')?.addEventListener("click", this.hide.bind(this), { signal: this._abortController.signal });
    Array.from(this.shadowRoot.querySelectorAll("slot")).forEach((slot) => this._updateSlotVisibility(slot));
  }
  get template() {
    return this.getAttribute("template") || "drawer-default-template";
  }
  get shouldLock() {
    return true;
  }
  get shouldAppendToBody() {
    return true;
  }
  get openFrom() {
    return window.matchMedia(`${window.themeVariables.breakpoints["sm-max"]}`).matches ? "bottom" : this.getAttribute("open-from") || "right";
  }
  _getClipPathProperties() {
    switch (this.openFrom) {
      case "left":
        return document.dir === "ltr" ? ["inset(0 100% 0 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"] : ["inset(0 0 0 100% round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
      case "right":
        return document.dir === "ltr" ? ["inset(0 0 0 100% round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm)"] : ["inset(0 100% 0 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
      case "bottom":
        return ["inset(100% 0 0 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
      case "top":
        return ["inset(0 0 100% 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
    }
  }
  _setInitialPosition() {
    this.style.left = document.dir === "ltr" && this.openFrom === "left" || document.dir === "rtl" && this.openFrom === "right" ? "0px" : "auto";
    this.style.right = this.style.left === "auto" ? "0px" : "auto";
    this.style.bottom = this.openFrom === "bottom" ? "0px" : null;
    this.style.top = this.style.bottom === "" ? "0px" : null;
  }
  _showTransition(animate19 = true) {
    let animationControls;
    this._setInitialPosition();
    if (reduceDrawerAnimation) {
      animationControls = motionAnimate(this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.2 });
    } else {
      let content = this.shadowRoot.querySelector('[part="content"]'), closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');
      animationControls = motionTimeline([
        [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
        [content, { clipPath: this._getClipPathProperties() }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
        [content.children, { opacity: [0, 1] }, { duration: 0.15 }],
        [closeButton, { opacity: [0, 1] }, { at: "<", duration: 0.15 }]
      ]);
    }
    animate19 ? animationControls.play() : animationControls.finish();
    return animationControls.finished.then(() => this.classList.add("show-close-cursor"));
  }
  _hideTransition() {
    let animationControls;
    if (reduceDrawerAnimation) {
      animationControls = motionAnimate(this, { opacity: [1, 0], visibility: ["visibility", "hidden"] }, { duration: 0.2 });
    } else {
      let content = this.shadowRoot.querySelector('[part="content"]'), closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');
      animationControls = motionTimeline([
        [closeButton, { opacity: [null, 0] }, { duration: 0.15 }],
        [content.children, { opacity: [null, 0] }, { at: "<", duration: 0.15 }],
        [content, { clipPath: this._getClipPathProperties().reverse() }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
        [this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 }]
      ]);
    }
    return animationControls.finished.then(() => this.classList.remove("show-close-cursor"));
  }
  _updateSlotVisibility(slot) {
    if (!["header", "footer", "body"].includes(slot.name)) {
      return;
    }
    slot.parentElement.hidden = slot.assignedElements({ flatten: true }).length === 0;
  }
};
if (!window.customElements.get("x-drawer")) {
  window.customElements.define("x-drawer", Drawer);
}

/**
 * Fix para asegurar que el gallery viewer siempre tenga altura correcta en mobile
 * SIEMPRE sobrescribe la altura en la primera carga con la altura real de la imagen
 * Después deja que Flickity maneje todo el comportamiento
 */
(function() {
  'use strict';
  
  let initialFixApplied = false; // Flag para saber si ya se aplicó la corrección inicial
  
  function calculateCorrectHeight() {
    const galleryViewer = document.querySelector('[data-gallery-viewer]');
    if (!galleryViewer) return null;
    
    const selectedFigure = galleryViewer.querySelector('[data-gallery-selected="true"]');
    if (!selectedFigure) return null;
    
    // Primero intentar obtener la altura del figure (si ya está renderizado correctamente)
    let height = selectedFigure.offsetHeight;
    
    // Si el figure tiene altura válida (ya está renderizado), usarla
    if (height > 2) {
      return height;
    }
    
    // Si no, calcular basándose en la imagen
    const img = selectedFigure.querySelector('img');
    if (img && img.naturalHeight && img.naturalWidth) {
      // Calcular altura basada en la imagen natural y el ancho del contenedor
      const viewerWidth = galleryViewer.offsetWidth;
      if (viewerWidth > 0) {
        const aspectRatio = img.naturalHeight / img.naturalWidth;
        height = viewerWidth * aspectRatio;
        return height;
      }
    }
    
    // Si la imagen no está cargada completamente, esperar
    return null;
  }
  
  function forceCorrectHeight() {
    if (window.innerWidth > 1023) return; // Solo en mobile
    if (initialFixApplied) return; // Ya se aplicó la corrección inicial
    
    const galleryViewer = document.querySelector('[data-gallery-viewer]');
    if (!galleryViewer) return;
    
    // Calcular la altura correcta basada en la imagen
    const correctHeight = calculateCorrectHeight();
    
    // Si no se pudo calcular aún (imagen no cargada), reintentar después
    if (correctHeight === null || correctHeight === 0) {
      return;
    }
    
    // SIEMPRE asignar la altura correcta la primera vez, sin importar lo que empire.js haya puesto
    galleryViewer.style.height = `${correctHeight}px`;
    initialFixApplied = true;
    console.log('forceCorrectHeight', correctHeight);
  }
  
  // Verificar inmediatamente al cargar
  function checkImmediately() {
    setTimeout(forceCorrectHeight, 100);
  }
  
  // Verificar después de un delay más largo (para dar tiempo a que las imágenes se carguen)
  function checkAfterDelay() {
    setTimeout(function() {
      if (!initialFixApplied) {
        forceCorrectHeight();
        
        // Si después de esto tampoco se aplicó, intentar una vez más
        if (!initialFixApplied) {
          setTimeout(forceCorrectHeight, 500);
        }
      }
    }, 300);
  }
  
  // Ejecutar al cargar el DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      checkImmediately();
      checkAfterDelay();
    });
  } else {
    checkImmediately();
    checkAfterDelay();
  }
  
  // Ejecutar cuando las imágenes se carguen (última oportunidad)
  window.addEventListener('load', function() {
    if (!initialFixApplied) {
      forceCorrectHeight();
    }
  });
  
  // Al cambiar de desktop a mobile, permitir una nueva corrección inicial
  let resizeTimer;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      const wasMobile = initialFixApplied;
      const isMobile = window.innerWidth <= 1023;
      
      // Si cambiamos de desktop a mobile, resetear para permitir corrección
      if (isMobile && !wasMobile) {
        initialFixApplied = false;
        setTimeout(forceCorrectHeight, 100);
      }
    }, 150);
  });
})();